import type { Language } from '@hero/schema';
import { formatNumberWithCommas, localizeVehicleType } from '@hero/core';
import { agentCopy } from '../language/agent-copy';

/** One cargo line as an edit card stages it. */
export type StagedLoadItem = {
  name?: string;
  type?: string;
  packing?: string;
  quantity?: number;
  weight?: { value: number; unit: string };
  cost?: number;
};

/** The load half of a staged order edit. */
export type StagedLoad = {
  items?: StagedLoadItem[];
  vehicleTypes?: string[];
  temperature?: { types?: string[] };
};

const WEIGHT_UNITS = ['kg', 'ton', 'gram'] as const;

const PACKING = ['pallet', 'package', 'unpackable_item', 'bag', 'barrel', 'other'] as const;

const ITEM_TYPES = [
  'food',
  'beauty',
  'perfume',
  'clothing',
  'textiles',
  'furniture',
  'appliances',
  'electronics',
  'paper',
  'plastic',
  'cleaning_materials',
  'medical_supplies',
  'medicines',
  'construction_materials',
  'chemicals',
  'spare_parts',
  'agriculture',
  'livestock',
  'petroleum',
  'mining',
  'waste',
  'vehicles',
  'dangerous_materials',
  'liquid',
  'other',
] as const;

const TEMPERATURES = ['dry', 'chilled', 'frozen', 'hot'] as const;

/** A key the copy has no label for is shown as-is. */
const isOneOf = <T extends string>(known: readonly T[], key: string): key is T =>
  (known as readonly string[]).includes(key);

const weightUnitLabel = (unit: string, language: Language): string =>
  isOneOf(WEIGHT_UNITS, unit) ? agentCopy(language, `orders.load.weight_units.${unit}`) : unit;

const packingLabel = (packing: string, language: Language): string =>
  isOneOf(PACKING, packing) ? agentCopy(language, `orders.load.packing.${packing}`) : packing;

const itemTypeLabel = (type: string, language: Language): string =>
  isOneOf(ITEM_TYPES, type) ? agentCopy(language, `orders.load.item_types.${type}`) : type;

const temperatureLabel = (type: string, language: Language): string =>
  isOneOf(TEMPERATURES, type) ? agentCopy(language, `orders.load.temperatures.${type}`) : type;

/** `20 طن`, `1.5 t` — the unit the user said, not a normalised one. */
export const formatLoadWeight = (
  weight: { value: number; unit: string } | undefined,
  language: Language,
): string | undefined =>
  weight
    ? `${formatNumberWithCommas(weight.value)} ${weightUnitLabel(weight.unit, language)}`
    : undefined;

/** `24 طبلية` — quantity with the packing word it counts. */
export const formatLoadQuantity = (
  item: Pick<StagedLoadItem, 'quantity' | 'packing'>,
  language: Language,
): string | undefined => {
  if (item.quantity == null) return undefined;
  const unit = item.packing ? packingLabel(item.packing, language) : undefined;
  return unit ? `${item.quantity} ${unit}` : String(item.quantity);
};

/**
 * Names come from `@hero/lang`'s `m-vehicle.json` via `@hero/core`,
 * imported as JSON rather than through i18next — which this module has
 * no instance of, running as it does on WhatsApp and voice. That was the
 * reason a second copy of the table used to live here, and a copy of a
 * translation is a translation that goes stale on one side.
 */
export const formatVehicleType = (type: string, language: Language): string =>
  localizeVehicleType(type, language);

/** User-facing type, or nothing when unclassified / `other`. */
export const displayVehicleType = (
  type: string | undefined,
  language: Language,
): string | undefined => {
  const raw = type?.trim();
  if (!raw || raw === 'other') return undefined;
  return formatVehicleType(raw, language);
};

export const formatTemperatureType = (type: string, language: Language): string =>
  temperatureLabel(type, language);

/**
 * One cargo line: `رز · مواد غذائية · 20 طن · 24 طبلية`.
 *
 * The category is shown next to the name even though it is usually
 * redundant to a human reader — it is guessed from the name, it lands on
 * the government waybill, and a guess nobody sees is a guess nobody
 * corrects.
 */
export const formatLoadItem = (item: StagedLoadItem, language: Language): string | undefined => {
  const name = item.name?.trim();
  const type = item.type ? itemTypeLabel(item.type, language) : undefined;
  const parts = [
    name || type,
    name && type ? type : undefined,
    formatLoadWeight(item.weight, language),
    formatLoadQuantity(item, language),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

/**
 * The whole load in one phrase — cargo, then the truck and temperature it
 * needs.
 *
 * One formatter for every surface: the WhatsApp confirmation and the
 * in-app card describe the same staged change, and a load that reads
 * "رز · 20 طن" in chat must not read "rice, 20000 kg" on the card.
 */
export const formatLoadSummary = (load: StagedLoad, language: Language): string | undefined => {
  const parts = (load.items ?? [])
    .map(item => formatLoadItem(item, language))
    .filter((line): line is string => Boolean(line));

  for (const type of load.temperature?.types ?? []) {
    parts.push(temperatureLabel(type, language));
  }
  for (const vehicle of load.vehicleTypes ?? []) {
    const label = displayVehicleType(vehicle, language);
    if (label) parts.push(label);
  }

  return parts.length > 0 ? parts.join(' · ') : undefined;
};
